import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { ArrowUpRight, BookOpen, Check, ExternalLink, History, Paperclip, Pencil, QrCode, Scissors, Stamp, UserPlus } from "lucide-react";
import { openPendingFile } from "../../../utils/download";
import { CancelDispatchButton } from "@/features/paper_send/components/CancelDispatchButton";
import { SplitIncomingForm } from "@/features/paper_send/components/SplitIncomingForm";
import { VademecumPanel } from "@/features/paper_send/components/VademecumPanel";
import {
  DISPATCHED_REGISTER_STATUSES,
  RegisterTrackingNumber,
} from "@/features/paper_send/components/TrackingNumber";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { DatePicker } from "@/components/ui/DatePicker";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  useCorrespondenceRegister,
  useRegisterCounterpartyOptions,
  useRegisterCounts,
  useRegisterMutations,
  useRegisterProjectOptions,
} from "@/features/power_projects/hooks";
import type {
  CorrespondenceRegister,
  DispatchStatus,
  QrSuggestion,
  RegisterChannel,
  RegisterDirection,
} from "@/features/power_projects/types";
import { REGISTER_CHANNEL_OPTIONS } from "@/features/power_projects/types";
import { fmtDate, fmtDateTime } from "@/features/power_projects/utils";
import {
  appendFiles,
  AttachmentLinks,
  DISPATCH_LABEL,
  DispatchBadge,
  DLC02_HUB_LINKS,
  HubNav,
  type HubBadge,
  MultiFileInput,
  PageHeader,
  RegisterDirectionBadge,
  TableShell,
  Tabs,
  Th,
} from "@/features/power_projects/components/ui";

// Paper (outgoing) lifecycle, kept from the dispatch queue. Incoming items are routed
// through dekretacja (received → filed) instead.
const NEXT: Partial<Record<DispatchStatus, DispatchStatus[]>> = {
  queued: ["prepared", "sent", "undeliverable"],
  prepared: ["sent", "undeliverable"],
  sent: ["delivered", "returned", "undeliverable"],
  returned: ["sent", "undeliverable"],
  delivered: [],
  undeliverable: ["sent"],
};

// ── legenda badgy z nawigacji — co znaczy każdy licznik + filtr po kliknięciu ──
// Keys mirror the /correspondence-register/counts/ payload; predicates mirror the backend
// queries behind each counter. Filtering is client-side because a counter can span several
// statuses (e.g. queued+prepared) that the single-value status select can't express.
type BadgeKey = "to_file" | "to_send" | "returned" | "to_acknowledge" | "my_dispatch";

const BADGE_INFO: Record<BadgeKey, { label: string; desc: string; tone: "primary" | "amber" | "red" }> = {
  to_file: {
    label: "Do zadekretowania",
    desc: "wpływ czekający na dekretację",
    tone: "amber",
  },
  to_send: {
    label: "Do wysłania",
    desc: "papierowe wychodzące w kolejce lub przygotowane do nadania",
    tone: "amber",
  },
  returned: {
    label: "Zwroty",
    desc: "papierowe wychodzące — zwrot lub niedoręczalne",
    tone: "red",
  },
  to_acknowledge: {
    label: "Do potwierdzenia odbioru",
    desc: "pisma zadekretowane do Ciebie, czekają na potwierdzenie",
    tone: "primary",
  },
  my_dispatch: {
    label: "Moje w kolejce wysyłki",
    desc: "pisma wysłane przez Ciebie — czekają na nadanie przez dział obiegu",
    tone: "amber",
  },
};

// `userId` matters only for to_acknowledge — an entry the user already confirmed stays out of
// their queue even when it was reopened for a newly added addressee (mirrors the backend counter).
const BADGE_PREDICATE: Record<BadgeKey, (r: CorrespondenceRegister, userId?: string) => boolean> = {
  to_file: (r) => r.direction === "incoming" && r.status === "received",
  to_send: (r) =>
    r.direction === "outgoing" && r.channel === "paper" && (r.status === "queued" || r.status === "prepared"),
  returned: (r) =>
    r.direction === "outgoing" && r.channel === "paper" && (r.status === "returned" || r.status === "undeliverable"),
  // On "Moja korespondencja" the list is already scoped to the current user, so the
  // status/direction check plus "not yet confirmed by me" matches the backend's counter.
  to_acknowledge: (r, userId) =>
    r.direction === "incoming" &&
    (r.status === "received" || r.status === "filed") &&
    !(userId && r.acknowledged_by.includes(userId)),
  // Zakładka „Moje wysyłane" jest już zawężona do wpisów tego autora, więc jak wyżej
  // wystarczy sprawdzić status (1:1 z backendowym licznikiem my_dispatch).
  my_dispatch: (r, userId) =>
    r.direction === "outgoing" &&
    (r.status === "queued" || r.status === "prepared") &&
    (!userId || r.registered_by === userId),
};

// Chip colours mirror the hub-nav badges (HubNav) and the sidebar tones.
const BADGE_TONE_CLASS: Record<"primary" | "amber" | "red", string> = {
  primary: "bg-[var(--color-primary)] text-[var(--color-text-inverse)]",
  amber: "bg-amber-500 text-white",
  red: "bg-red-500 text-white",
};

type LegendItem = {
  key: BadgeKey;
  count: number;
  // true → the counter's items live in THIS view and a click filters the table;
  // false → they live on the other tab, so a click navigates there (with ?badge=).
  local: boolean;
  navHint?: string;
};

function BadgeLegend({
  items,
  activeKey,
  onToggle,
  onNavigate,
}: {
  items: LegendItem[];
  activeKey: "" | BadgeKey;
  onToggle: (key: BadgeKey) => void;
  onNavigate: (key: BadgeKey) => void;
}) {
  if (!items.length) return null;
  return (
    <div className="mb-4 -mt-1 flex flex-wrap items-center gap-x-2 gap-y-1.5">
      <span className="section-label">Oznaczenia:</span>
      {items.map((it) => {
        const info = BADGE_INFO[it.key];
        const active = it.local && activeKey === it.key;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => (it.local ? onToggle(it.key) : onNavigate(it.key))}
            title={
              it.local
                ? active
                  ? "Filtr aktywny — kliknij, aby wyczyścić"
                  : "Kliknij, aby pokazać w tabeli tylko te pozycje"
                : it.navHint
            }
            className={clsx(
              "inline-flex items-center gap-1.5 border px-2 py-1 text-[11px] transition-colors",
              active
                ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]"
                : "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-text-primary)]",
            )}
          >
            <span
              className={clsx(
                "rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none",
                BADGE_TONE_CLASS[info.tone],
              )}
            >
              {it.count}
            </span>
            <span className="font-medium">{info.label}</span>
            <span className="hidden sm:inline text-[var(--color-text-muted)]">— {info.desc}</span>
            {active && <span className="font-bold text-[var(--color-primary)]">×</span>}
            {!it.local && <ArrowUpRight size={11} className="text-[var(--color-text-muted)]" />}
          </button>
        );
      })}
    </div>
  );
}

// ── rejestracja wpływu (incoming) — also powers edit of a not-yet-decreed entry ──
function IncomingForm({
  userOptions,
  projectOptions,
  projectCoordinators,
  onDone,
  editRow,
  selfOnly = false,
  currentUserId,
}: {
  userOptions: { value: string; label: string }[];
  projectOptions: { value: string; label: string }[];
  // projectId → coordinator user ids; drives auto-select + pin-to-top in "Cel — osoby".
  projectCoordinators: Record<string, string[]>;
  onDone: () => void;
  // When set, the form edits this RECEIVED (niezadekretowany) entry instead of creating one.
  editRow?: CorrespondenceRegister;
  // "Moja korespondencja" without the register permission: the letter is registered FOR the
  // current user only (target locked to them, projects limited by the parent to coordinated
  // ones; backend re-enforces both).
  selfOnly?: boolean;
  currentUserId?: string;
}) {
  const m = useRegisterMutations();
  const [channel, setChannel] = useState<RegisterChannel>(editRow?.channel ?? "paper");
  const [counterparty, setCounterparty] = useState(editRow?.counterparty_name ?? "");
  const [address, setAddress] = useState(editRow?.counterparty_address ?? "");
  const [date, setDate] = useState(editRow?.date ?? "");
  const [tracking, setTracking] = useState(editRow?.tracking_number ?? "");
  const [note, setNote] = useState(editRow?.note ?? "");
  const [targetUsers, setTargetUsers] = useState<string[]>(
    editRow?.target_users ?? (selfOnly && currentUserId ? [currentUserId] : []),
  );
  const [targetProject, setTargetProject] = useState(editRow?.target_project ?? "");
  const [files, setFiles] = useState<File[]>([]);

  // Coordinators of the picked project stay pinned on top of the OSOBY list for as long
  // as their project is selected — even after the user deselects them.
  const pinnedCoordinators = useMemo(
    () => (targetProject ? projectCoordinators[targetProject] ?? [] : []),
    [targetProject, projectCoordinators],
  );
  const personOptions = useMemo(() => {
    if (!pinnedCoordinators.length) return userOptions;
    const pinned = userOptions
      .filter((o) => pinnedCoordinators.includes(o.value))
      .map((o) => ({ ...o, label: `${o.label} (koordynator)` }));
    const rest = userOptions.filter((o) => !pinnedCoordinators.includes(o.value));
    return [...pinned, ...rest];
  }, [userOptions, pinnedCoordinators]);

  // Picking a project auto-selects its coordinators (still deselectable); switching to
  // another project swaps them out so a mis-click can't silently decree to the wrong people.
  // In selfOnly mode the target is locked to the current user, so only the project changes.
  const changeProject = (projectId: string) => {
    if (selfOnly) {
      setTargetProject(projectId);
      return;
    }
    const prevCoords = targetProject ? projectCoordinators[targetProject] ?? [] : [];
    const nextCoords = projectId ? projectCoordinators[projectId] ?? [] : [];
    setTargetProject(projectId);
    setTargetUsers((prev) => [
      ...prev.filter((id) => !prevCoords.includes(id) || nextCoords.includes(id)),
      ...nextCoords.filter((id) => !prev.includes(id)),
    ]);
  };
  // Existing scans marked for removal (edit mode) — at least one attachment must remain.
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const mutation = editRow ? m.updateIncoming : m.registerIncoming;
  // A registered incoming item without a scan is meaningless, so require ≥ 1 file.
  const keptCount = (editRow?.files.length ?? 0) - removedIds.size;
  const canSubmit = keptCount + files.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    const fd = new FormData();
    fd.append("channel", channel);
    if (counterparty) fd.append("counterparty_name", counterparty);
    if (editRow) {
      // Edit sends the FULL form state — blank-able fields go as "" so clearing works.
      fd.append("counterparty_address", address);
      fd.append("tracking_number", tracking);
      fd.append("note", note);
      fd.append("target_project", targetProject);
      removedIds.forEach((id) => fd.append("remove_files", id));
    } else {
      if (address) fd.append("counterparty_address", address);
      if (tracking) fd.append("tracking_number", tracking);
      if (note) fd.append("note", note);
      if (targetProject) fd.append("target_project", targetProject);
    }
    if (date) fd.append("date", date);
    targetUsers.forEach((uid) => fd.append("target_users", uid));
    appendFiles(fd, files);
    if (editRow) m.updateIncoming.mutate({ id: editRow.id, fd }, { onSuccess: onDone });
    else m.registerIncoming.mutate(fd, { onSuccess: onDone });
  };

  return (
    <div className="card p-3 mb-4 space-y-3">
      <h3 className="section-label">
        {editRow ? "Edycja wpływu (niezadekretowanego)" : "Rejestracja wpływu (przychodzące)"}
      </h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="section-label">Kanał</span>
          <select className="input-base mt-1" value={channel} onChange={(e) => setChannel(e.target.value as RegisterChannel)}>
            {REGISTER_CHANNEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="section-label">Data wpływu</span>
          <DatePicker className="mt-1" value={date} onChange={setDate} />
        </label>
        <label className="block">
          <span className="section-label">Nadawca</span>
          <input className="input-base mt-1" placeholder="Nadawca / urząd" value={counterparty} onChange={(e) => setCounterparty(e.target.value)} />
        </label>
        <label className="block">
          <span className="section-label">Adres nadawcy</span>
          <input className="input-base mt-1" value={address} onChange={(e) => setAddress(e.target.value)} />
        </label>
        <label className="block">
          <span className="section-label">Nr przesyłki / znak</span>
          <input className="input-base mt-1" value={tracking} onChange={(e) => setTracking(e.target.value)} />
        </label>
        <label className="block sm:col-span-2">
          <span className="section-label">Uwagi</span>
          <input className="input-base mt-1" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="block">
          <span className="section-label">Cel — osoby {selfOnly ? "" : "(opcjonalnie)"}</span>
          <div className="mt-1">
            {selfOnly ? (
              <div className="text-[12px] text-[var(--color-text-secondary)] border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1.5">
                Ty — list rejestrowany na siebie
              </div>
            ) : (
              <MultiSelect options={personOptions} value={targetUsers} placeholder="— bez dekretacji —" onChange={setTargetUsers} />
            )}
          </div>
        </div>
        <div className="block">
          <span className="section-label">Cel — projekt (opcjonalnie)</span>
          <div className="mt-1">
            <SearchableSelect
              options={projectOptions}
              value={targetProject}
              placeholder={selfOnly ? "— moje przedsięwzięcia —" : "— bez projektu —"}
              onChange={(e) => changeProject(e.target.value)}
            />
          </div>
        </div>
      </div>
      {editRow && editRow.files.length > 0 && (
        <div className="space-y-1">
          <span className="section-label">Obecne załączniki</span>
          <ul className="space-y-0.5">
            {editRow.files.map((f) => {
              const removed = removedIds.has(f.id);
              return (
                <li key={f.id} className="flex items-center justify-between gap-2 text-[11px] font-mono">
                  <span className={removed ? "line-through text-[var(--color-text-muted)] truncate" : "text-[var(--color-text-secondary)] truncate"}>
                    {f.original_filename}
                  </span>
                  <button
                    type="button"
                    className="text-[var(--color-primary)] hover:underline shrink-0"
                    onClick={() =>
                      setRemovedIds((prev) => {
                        const next = new Set(prev);
                        if (removed) next.delete(f.id);
                        else next.add(f.id);
                        return next;
                      })
                    }
                  >
                    {removed ? "przywróć" : "usuń"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <MultiFileInput
        files={files}
        onChange={setFiles}
        label={editRow ? "Nowe skany / załączniki (opcjonalnie)" : "Skany / załączniki (wymagane)"}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!canSubmit} loading={mutation.isPending} onClick={submit}>
          {editRow ? "Zapisz zmiany" : "Zarejestruj wpływ"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
        {!canSubmit && (
          <span className="section-label text-[var(--color-primary)]">
            {editRow ? "Wpływ musi mieć co najmniej jeden skan" : "Dodaj co najmniej jeden skan"}
          </span>
        )}
        {mutation.isError && (
          <span className="section-label text-red-600">Nie udało się zapisać. Spróbuj ponownie.</span>
        )}
      </div>
    </div>
  );
}

// ── QR sugestia — chip z rozpoznania skanów wpływu (tylko informacja) ─────────
function qrSuggestionOf(row: CorrespondenceRegister): QrSuggestion | null {
  return row.qr_scan_status === "matched" ? (row.qr_suggestion as QrSuggestion) : null;
}

function QrChips({ row }: { row: CorrespondenceRegister }) {
  if (row.qr_scan_status === "pending") {
    return <div className="text-[11px] text-[var(--color-text-muted)] mt-1">Rozpoznawanie QR…</div>;
  }
  const q = qrSuggestionOf(row);
  if (!q) return null; // unmatched/failed — bez szumu, dekretacja jak dotychczas
  const labels = q.route_version_label && q.current_route_version_label
    ? `${q.route_version_label} → ${q.current_route_version_label}`
    : "";
  const diff = q.route_scope_diff;
  return (
    <div className="mt-1 space-y-0.5">
      <div className="inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-300/60 px-1.5 py-0.5 rounded">
        <QrCode size={11} />
        <span>
          {q.project_name || "projekt"}
          {q.application_number ? ` · wniosek ${q.application_number}` : ""}
        </span>
      </div>
      {q.route_current === false && labels && (
        diff?.changed === true ? (
          <details className="text-[11px] text-amber-700">
            <summary className="cursor-pointer inline-block border border-amber-300/60 px-1.5 py-0.5 rounded">
              Trasa zmieniona w zakresie wniosku: {labels}
            </summary>
            <div className="pl-2 pt-0.5 text-[var(--color-text-secondary)]">{diff.summary}</div>
          </details>
        ) : diff?.changed === false ? (
          <div className="text-[11px] text-[var(--color-text-muted)]">
            Nowsza wersja trasy ({labels}), zakres wniosku bez zmian
          </div>
        ) : (
          <div className="inline-block text-[11px] text-amber-700 border border-amber-300/60 px-1.5 py-0.5 rounded">
            Trasa NIEAKTUALNA: {labels} — zweryfikuj zakres
          </div>
        )
      )}
    </div>
  );
}

// ── dekretacja — route an incoming item to a person/project, mark FILED ───────
function FileIncoming({
  row,
  userOptions,
  projectOptions,
  contractors = {},
  selfOnly = false,
}: {
  row: CorrespondenceRegister;
  userOptions: { value: string; label: string }[];
  projectOptions: { value: string; label: string }[];
  /** projectId → firma prowadząca. Wskazanie takiego projektu kieruje pismo do jej rejestru. */
  contractors?: Record<string, string>;
  // Samodekretacja ("Moja korespondencja"): an addressee decrees the letter onto its CURRENT
  // addressees — the set stays locked (backend refuses dropping anybody; whoever wants only
  // their part splits first); the project is any ZE project (parity with AssignProject,
  // which lets an addressee pin one right after dekretacja).
  selfOnly?: boolean;
}) {
  const m = useRegisterMutations();
  const [open, setOpen] = useState(false);
  const [targetUsers, setTargetUsers] = useState<string[]>(row.target_users ?? []);
  // Prefill z sugestii QR tylko, gdy operator faktycznie MA ten projekt w opcjach —
  // ślepy UUID spoza portfolio zostawiłby selektor w niezrozumiałym stanie.
  const qr = qrSuggestionOf(row);
  const qrProject =
    qr?.project_id && projectOptions.some((o) => o.value === qr.project_id) ? qr.project_id : "";
  const [targetProject, setTargetProject] = useState(row.target_project ?? qrProject);
  const [note, setNote] = useState("");

  if (!open) {
    return (
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        <Stamp size={13} />
        Dekretuj
      </Button>
    );
  }

  // Projekt prowadzony przez firmę kieruje pismo do jej rejestru spraw — bez wyboru adresata
  // i bez dekretacji na nasze biurka.
  const contractorName = targetProject ? contractors[targetProject] : undefined;
  const canFile = Boolean(contractorName) || targetUsers.length > 0 || Boolean(targetProject);

  return (
    <div
      className={clsx(
        "w-[260px] max-w-full border border-[var(--color-border)] border-l-[3px] bg-[var(--color-bg-surface)] p-2.5 space-y-2.5",
        contractorName ? "border-l-[#1d4ed8]" : "border-l-[var(--color-primary)]",
      )}
    >
      <div className="flex items-center gap-1.5 section-label text-[var(--color-text-secondary)]">
        <Stamp size={12} className={contractorName ? "text-[#1d4ed8]" : "text-[var(--color-primary)]"} />
        Dekretacja pisma
      </div>
      <SearchableSelect
        label="Projekt / sprawa"
        options={projectOptions}
        value={targetProject}
        placeholder="— wskaż projekt —"
        onChange={(e) => {
          const next = e.target.value;
          setTargetProject(next);
          // Przejście na projekt firmy zdejmuje wskazane osoby — pismo idzie na zewnątrz.
          if (contractors[next]) setTargetUsers([]);
        }}
      />
      {contractorName && (
        <div className="border-l-[3px] border-l-[#1d4ed8] bg-[#eef2ff] px-2 py-1.5 text-[11.5px] leading-snug dark:bg-[#11172e]">
          <div className="font-semibold text-[12px]">Pismo trafi do firmy {contractorName}.</div>
          Projekt prowadzi firma zewnętrzna — wpis idzie do jej rejestru spraw wraz ze skanem,
          zwrotką i załącznikami.
        </div>
      )}
      <div className="space-y-1">
        <span className="section-label">
          Komórka / osoby{contractorName && <span className="ml-1 normal-case">· zablokowane</span>}
        </span>
        {contractorName ? (
          <div className="border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1.5 text-[12px] text-[var(--color-text-muted)]">
            nie dotyczy — adresatem jest firma prowadząca
          </div>
        ) : selfOnly ? (
          <div className="text-[12px] text-[var(--color-text-secondary)] border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1.5">
            {row.target_users.length > 1
              ? `Adresaci: ${row.target_user_names.join(", ")}`
              : "Ty — dekretacja na siebie"}
          </div>
        ) : (
          <MultiSelect
            options={userOptions}
            value={targetUsers}
            placeholder="— wskaż osoby —"
            onChange={setTargetUsers}
          />
        )}
      </div>
      {qr?.project_name && (
        <div className="flex items-center gap-1 text-[11px] text-emerald-700">
          <QrCode size={11} />
          <span>
            Podpowiedź z QR: {qr.project_name}
            {qr.application_number ? ` · wniosek ${qr.application_number}` : ""} — zweryfikuj i zatwierdź
          </span>
        </div>
      )}
      <label className="block space-y-1">
        <span className="section-label">Adnotacja</span>
        <input
          className="input-base text-xs py-1.5"
          placeholder="np. proszę o realizację do 30.06"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="flex gap-1.5 pt-0.5">
        <Button
          size="sm"
          disabled={!canFile}
          loading={m.fileIncoming.isPending}
          onClick={() =>
            m.fileIncoming.mutate(
              { id: row.id, target_users: targetUsers, target_project: targetProject || null, note },
              { onSuccess: () => setOpen(false) },
            )
          }
        >
          <Stamp size={13} />
          {contractorName ? "Zadekretuj do firmy" : "Zadekretuj"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Anuluj</Button>
      </div>
    </div>
  );
}

// ── wycofanie przekazania — pismo znika z rejestru firmy, ślad zostaje w dzienniku projektu
function WithdrawFromContractor({ row }: { row: CorrespondenceRegister }) {
  const m = useRegisterMutations();
  const [powod, setPowod] = useState<string | null>(null);

  if (powod === null) {
    return (
      <button
        type="button"
        className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:underline"
        onClick={() => setPowod("")}
      >
        wycofaj przekazanie
      </button>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      <input
        className="input-base w-44 py-1 text-[11px]"
        autoFocus
        placeholder="powód wycofania"
        value={powod}
        onChange={(e) => setPowod(e.target.value)}
      />
      <Button
        size="sm"
        loading={m.withdrawFromContractor.isPending}
        onClick={() =>
          m.withdrawFromContractor.mutate(
            { id: row.id, reason: powod },
            { onSuccess: () => setPowod(null) },
          )
        }
      >
        Wycofaj
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setPowod(null)}>Anuluj</Button>
    </span>
  );
}

// ── podpięcie / odpięcie projektu — autor wpisu albo adresat dekretacji (dział obiegu:
// dowolny wpis) przypina wpływ do przedsięwzięcia ZE; po dekretacji skany lądują w poufnym
// folderze KORESPONDENCJA w plikach projektu. Odpina wyłącznie adresat dekretacji albo
// dyrektor — wtedy lustrzane pliki znikają z projektu (backend re-enforces both).
function AssignProject({
  row,
  options,
  canAssign,
  canUnassign,
}: {
  row: CorrespondenceRegister;
  options: { value: string; label: string }[];
  canAssign: boolean;
  canUnassign: boolean;
}) {
  const m = useRegisterMutations();
  const [open, setOpen] = useState(false);
  const [project, setProject] = useState(row.target_project ?? "");

  if (!open) {
    return (
      <span className="flex flex-wrap items-center gap-x-2">
        {canAssign && !!options.length && (
          <button
            type="button"
            className="text-[11px] text-[var(--color-primary)] hover:underline"
            onClick={() => setOpen(true)}
          >
            {row.target_project ? "zmień projekt" : "+ podepnij projekt"}
          </button>
        )}
        {canUnassign && row.target_project && (
          <button
            type="button"
            className="text-[11px] text-[var(--color-primary)] hover:underline"
            title="Odepnij projekt od wpisu — skany znikną z plików przedsięwzięcia (także z folderu, do którego je przeniesiono)"
            disabled={m.unassignProject.isPending}
            onClick={() => {
              if (confirm(`Odpiąć projekt „${row.target_project_name ?? ""}" od tego wpisu? Pliki znikną z zakładki Pliki przedsięwzięcia.`)) {
                m.unassignProject.mutate(row.id);
              }
            }}
          >
            odepnij
          </button>
        )}
        {m.unassignProject.isError && (
          <span className="block w-full text-[11px] text-red-600">Nie udało się odpiąć projektu.</span>
        )}
      </span>
    );
  }
  return (
    <div className="w-[220px] max-w-full space-y-1.5">
      <SearchableSelect
        options={options}
        value={project}
        placeholder="— wybierz projekt —"
        onChange={(e) => setProject(e.target.value)}
      />
      <div className="flex gap-1.5">
        <Button
          size="sm"
          disabled={!project || project === row.target_project}
          loading={m.assignProject.isPending}
          onClick={() =>
            m.assignProject.mutate(
              { id: row.id, target_project: project },
              { onSuccess: () => setOpen(false) },
            )
          }
        >
          Podepnij
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Anuluj</Button>
      </div>
      {m.assignProject.isError && (
        <span className="block text-[11px] text-red-600">Nie udało się podpiąć projektu.</span>
      )}
    </div>
  );
}

// ── adresaci dekretacji — kto ma pismo u siebie i kto już potwierdził odbiór ───
// Zbiór adresatów nie jest zamknięty na dekretacji: obecny adresat (albo dyrektor / dział
// obiegu) dodaje kolejne osoby, a dyrektor / dział obiegu może kogoś zdjąć. Dodanie osoby do
// wpisu z potwierdzonym odbiorem wraca go do statusu „Zadekretowane" — ale tylko dla nowej
// osoby: kto już potwierdził, nie jest pytany drugi raz (backend: acknowledged_by).
function Recipients({
  row,
  userOptions,
  canAdd,
  canRemove,
}: {
  row: CorrespondenceRegister;
  userOptions: { value: string; label: string }[];
  canAdd: boolean;
  canRemove: boolean;
}) {
  const m = useRegisterMutations();
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const options = useMemo(
    () => userOptions.filter((o) => !row.target_users.includes(o.value)),
    [userOptions, row.target_users],
  );
  const closeForm = () => {
    setPicked([]);
    setAdding(false);
  };

  if (!row.target_users.length && !canAdd) return null;

  return (
    <div className="mt-1 space-y-0.5">
      {row.target_users.map((id, i) => {
        const acked = row.acknowledged_by.includes(id);
        return (
          <div key={id} className="flex items-baseline gap-1 text-[11px] font-mono">
            <span
              className={acked ? "text-emerald-600" : "text-[var(--color-text-secondary)]"}
              title={acked ? "Potwierdził odbiór" : "Nie potwierdził jeszcze odbioru"}
            >
              {acked ? "✓" : "→"} {row.target_user_names[i] ?? id}
            </span>
            {/* Ostatniego adresata nie da się usunąć — wpis bez dekretacji zniknąłby ze
                wszystkich „Moich korespondencji" (backend re-enforces). */}
            {canRemove && row.target_users.length > 1 && (
              <button
                type="button"
                className="text-[var(--color-primary)] hover:underline"
                title="Usuń adresata z dekretacji"
                disabled={m.removeRecipients.isPending}
                onClick={() => m.removeRecipients.mutate({ id: row.id, target_users: [id] })}
              >
                usuń
              </button>
            )}
          </div>
        );
      })}
      {m.removeRecipients.isError && (
        <span className="block text-[11px] text-red-600">Nie udało się usunąć adresata.</span>
      )}
      {canAdd &&
        (adding ? (
          <div className="w-[220px] max-w-full space-y-1.5 pt-1">
            <MultiSelect
              options={options}
              value={picked}
              placeholder="— wskaż osoby —"
              onChange={setPicked}
            />
            {row.status === "acknowledged" && (
              <span className="block text-[11px] text-[var(--color-text-secondary)]">
                Odbiór jest już potwierdzony — dodanie adresata wróci wpis do statusu
                „Zadekretowane", żeby nowa osoba potwierdziła swój odbiór.
              </span>
            )}
            <div className="flex gap-1.5">
              <Button
                size="sm"
                disabled={!picked.length}
                loading={m.addRecipients.isPending}
                onClick={() =>
                  m.addRecipients.mutate(
                    { id: row.id, target_users: picked },
                    { onSuccess: closeForm },
                  )
                }
              >
                <UserPlus size={13} />
                Dodaj
              </Button>
              <Button size="sm" variant="ghost" onClick={closeForm}>Anuluj</Button>
            </div>
            {m.addRecipients.isError && (
              <span className="block text-[11px] text-red-600">Nie udało się dodać adresata.</span>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="text-[11px] text-[var(--color-primary)] hover:underline"
            title="Dodaj kolejną osobę do dekretacji — zobaczy pismo w swojej korespondencji i potwierdzi odbiór"
            onClick={() => setAdding(true)}
          >
            + dodaj adresata
          </button>
        ))}
    </div>
  );
}

// ── potwierdzenie odbioru — only the dekretacja target sees this ──────────────
function AcknowledgeButton({
  row,
  currentUserId,
}: {
  row: CorrespondenceRegister;
  currentUserId?: string;
}) {
  const m = useRegisterMutations();
  // Przesyłka zbiorcza: potwierdzenie zamyka CAŁY wpis, także za adresatów, którzy jeszcze nie
  // potwierdzili — powiedz to wprost i wskaż podział jako drogę do potwierdzenia własnej części.
  const pendingOthers = row.target_users.filter(
    (id) => id !== currentUserId && !row.acknowledged_by.includes(id),
  ).length;
  return (
    <div className="space-y-1">
      <Button
        size="sm"
        variant="secondary"
        loading={m.acknowledge.isPending}
        onClick={() => m.acknowledge.mutate(row.id)}
      >
        <Check size={13} />
        Oznacz jako odebrane
      </Button>
      {pendingOthers > 0 && (
        <div className="text-[11px] text-[var(--color-text-secondary)] max-w-[220px]">
          Odbioru nie potwierdziła jeszcze {pendingOthers === 1 ? "1 osoba" : `${pendingOthers} osoby`} —
          Twoje potwierdzenie zamknie wpis także za nią. Chcesz potwierdzić tylko swoją część?
          Użyj „Podziel”.
        </div>
      )}
    </div>
  );
}

// ── historia akcji wpisu — kto i kiedy wykonał każdą akcję (modal) ────────────
function EventLine({ ev }: { ev: CorrespondenceRegister["events"][number] }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px]">
      <span className="w-[120px] shrink-0 font-mono text-[11px] text-[var(--color-text-muted)]">
        {fmtDateTime(ev.changed_at)}
      </span>
      <span className="font-medium text-[var(--color-text-primary)]">{ev.changed_by_name}</span>
      <span className="text-[var(--color-text-secondary)]">
        {/* Status change → arrow with PL labels; same-status/blank → the note carries the
            action (edit, dekretacja do projektu itp.), so only the status context shows. */}
        {ev.from_status && ev.from_status !== ev.to_status
          ? `${DISPATCH_LABEL[ev.from_status]} → ${DISPATCH_LABEL[ev.to_status]}`
          : DISPATCH_LABEL[ev.to_status]}
        {ev.note && <span className="text-[var(--color-text-muted)]"> · {ev.note}</span>}
      </span>
    </li>
  );
}

function EntryHistory({ row }: { row: CorrespondenceRegister }) {
  // A part created by a split carries a copy of its source entry's history — collapsed by
  // default, so the part's own timeline stays front and center.
  const inherited = row.events.filter((ev) => ev.inherited);
  const own = row.events.filter((ev) => !ev.inherited);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pb-2 border-b border-[var(--color-border-muted)]">
        <RegisterDirectionBadge direction={row.direction} />
        <span className="font-medium text-[var(--color-text-primary)]">{row.counterparty_name || "—"}</span>
        <span className="text-[11px] font-mono text-[var(--color-text-muted)]">{row.channel_display}</span>
        <DispatchBadge status={row.status} />
      </div>
      {inherited.length > 0 && (
        <details className="border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] px-3 py-2">
          <summary className="cursor-pointer section-label text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]">
            Historia wpisu źródłowego (przed podziałem) · {inherited.length}
          </summary>
          <ul className="space-y-2 mt-2">
            {inherited.map((ev) => <EventLine key={ev.id} ev={ev} />)}
          </ul>
        </details>
      )}
      {row.events.length === 0 ? (
        <div className="section-label py-4 text-center">Brak zapisanych akcji.</div>
      ) : (
        <ul className="space-y-2">
          {own.map((ev) => <EventLine key={ev.id} ev={ev} />)}
        </ul>
      )}
    </div>
  );
}

function RegisterRow({
  row,
  userOptions,
  projectOptions,
  projectContractors = {},
  currentUserId,
  canManage = false,
  isDirector = false,
  canOpenProject = false,
  selectable = false,
  selectedFiles,
  onToggleFile,
  assignProjectOptions,
  onEdit,
  onSplit,
  onHistory,
}: {
  row: CorrespondenceRegister;
  userOptions: { value: string; label: string }[];
  projectOptions: { value: string; label: string }[];
  /** projectId → firma prowadząca projekt. */
  projectContractors?: Record<string, string>;
  currentUserId?: string;
  // 'Firmowy obieg dokumentów' holder — may dekretować; directors browse read-only.
  canManage?: boolean;
  // Dyrektor / prezes — jedyny (poza adresatami wpisu), kto dokłada adresatów po dekretacji.
  isDirector?: boolean;
  // DLC_01 access — without it the project name is plain text (the workspace would refuse them).
  canOpenProject?: boolean;
  // When set (Moja korespondencja), each attachment gets a checkbox for bulk ZIP download.
  selectable?: boolean;
  selectedFiles?: Set<string>;
  onToggleFile?: (id: string) => void;
  // Projekty ZE do podpięcia pod wpis (project-options — pełny słownik dla każdego
  // użytkownika DLC_02); kto może podpiąć/odpiąć rozstrzyga RegisterRow + backend.
  assignProjectOptions?: { value: string; label: string }[];
  // When set (full register), a RECEIVED incoming row gets an "Edytuj" action.
  onEdit?: (row: CorrespondenceRegister) => void;
  // When set, an incoming row gets a "Podziel" (split) action — for the dział obiegu on any
  // entry, and for an addressee on entries decreed to them (see canSplit below).
  onSplit?: (row: CorrespondenceRegister) => void;
  // Historia akcji wpisu — opens the who-did-what modal; available to every viewer.
  onHistory: (row: CorrespondenceRegister) => void;
}) {
  const m = useRegisterMutations();
  const conf = row.confirmation_file;
  const [tracking, setTracking] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const nextStatuses = NEXT[row.status] ?? [];
  const isOutgoing = row.direction === "outgoing";
  const isPaper = row.channel === "paper";
  const isAddressee = !!currentUserId && row.target_users.includes(currentUserId);
  // Autor wpisu (registered_by) — może podpiąć projekt tak samo jak adresat dekretacji.
  const isAuthor = !!currentUserId && row.registered_by === currentUserId;
  // Podpięcie: autor / adresat / dział obiegu; odpięcie: WYŁĄCZNIE adresat albo dyrektor
  // (dział obiegu bez roli dyrektora nie odpina — backend re-enforces both).
  const canAssignProject = !isOutgoing && (canManage || isAddressee || isAuthor);
  const canUnassignProject = !isOutgoing && !!row.target_project && (isAddressee || isDirector);
  // Podział wpływu — możliwy do momentu potwierdzenia odbioru. Poza działem obiegu dokumentów
  // dzieli też adresat dekretacji: przesyłkę zadekretowaną na kilka osób rozdziela się na
  // pozycje, żeby każdy potwierdzał odbiór wyłącznie swojej części (backend re-enforces).
  const canSplit =
    !isOutgoing &&
    (row.status === "received" || row.status === "filed") &&
    row.acknowledged_by.length === 0 &&
    (canManage || isAddressee);
  const iAcknowledged = !!currentUserId && row.acknowledged_by.includes(currentUserId);
  // Dodawanie / usuwanie adresatów jest możliwe po dekretacji — także po potwierdzeniu odbioru
  // (dodanie wraca wpis do „Zadekretowane" dla nowej osoby). Dodaje obecny adresat albo dyrektor;
  // usuwa dyrektor / dział obiegu dokumentów (backend re-enforces both). Dział obiegu adresatów
  // nie dokłada — dekretuje przy rejestracji, a potem może kogoś tylko zdjąć.
  const afterDekretacja = !isOutgoing && (row.status === "filed" || row.status === "acknowledged");
  const canAddRecipients = afterDekretacja && (isDirector || isAddressee);
  const canRemoveRecipients = afterDekretacja && canManage;

  const doTransition = (to: DispatchStatus) => {
    const fd = new FormData();
    fd.append("to_status", to);
    if (tracking) fd.append("tracking_number", tracking);
    if (file && (to === "delivered" || to === "returned")) fd.append("confirmation_file", file);
    m.transition.mutate({ id: row.id, fd });
  };

  return (
    <tr className="border-t border-[var(--color-border-muted)] align-top hover:bg-[var(--color-bg-elevated)] transition-colors">
      <td className="px-3 py-2.5">
        <RegisterDirectionBadge direction={row.direction} />
        <div className="text-[10px] font-mono text-[var(--color-text-muted)] mt-1">{row.channel_display}</div>
        {/* Arrival / posting date — kept here, not under the status, so it doesn't read as the
            date of the current status (e.g. confirmation of receipt). */}
        {row.date && (
          <div className="text-[10px] font-mono text-[var(--color-text-muted)] mt-0.5 whitespace-nowrap">
            {isOutgoing ? "nadanie" : "wpływ"}: {fmtDate(row.date)}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        {/* Capped so a long address/note wraps instead of widening the register past the viewport. */}
        <div className="max-w-[220px] break-words">
          <div className="font-medium text-[var(--color-text-primary)]">{row.counterparty_name || "—"}</div>
          {row.counterparty_address && <div className="text-[11px] text-[var(--color-text-secondary)]">{row.counterparty_address}</div>}
          {row.source_label && <div className="text-[11px] font-mono text-[var(--color-text-muted)]">{row.source_label}</div>}
          {row.note && <div className="text-[11px] text-[var(--color-text-secondary)]">{row.note}</div>}
          <QrChips row={row} />
        </div>
      </td>
      <td className="px-3 py-2.5">
        {canOpenProject && row.target_project_has_ze && row.target_project_name && row.target_project
          ? (
            // Opens the project workspace in a NEW tab — the user is mid-register (filters,
            // selections) and shouldn't lose that context just to peek at the project.
            <a
              href={`/power-projects/${row.target_project}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-primary)] hover:underline"
              title="Otwórz projekt w nowej karcie"
            >
              {row.target_project_name}
              <ExternalLink className="h-3 w-3 shrink-0 text-[var(--color-text-muted)]" aria-hidden />
            </a>
          )
          : row.target_project_name && row.target_project
            ? <span className="text-[13px] font-semibold text-[var(--color-text-primary)]">{row.target_project_name}</span>
          : row.target_project_name
            ? <span className="text-[11px] text-[var(--color-text-secondary)]">{row.target_project_name}</span>
            : <span className="text-[var(--color-text-muted)]">—</span>}
        {/* Pismo przekazane firmie prowadzącej projekt — niebieski, żeby nie mylić go
            z dekretacją na nasze biurka. */}
        {row.led_register_label && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="border border-[#1d4ed8] px-1.5 py-px font-mono text-[9.5px] uppercase tracking-wider text-[#1d4ed8]">
              przekazane firmie · {row.led_contractor_name}
            </span>
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
              {row.led_register_label}
            </span>
            <WithdrawFromContractor row={row} />
          </div>
        )}
        {/* Podpięcie / odpięcie projektu na wpisie przychodzącym — patrz AssignProject. */}
        {(canAssignProject || canUnassignProject) && (
          <div className="mt-1">
            <AssignProject
              row={row}
              options={assignProjectOptions ?? []}
              canAssign={canAssignProject}
              canUnassign={canUnassignProject}
            />
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        {row.registered_by_name ? (
          <>
            <div className="text-[12px] text-[var(--color-text-secondary)]">{row.registered_by_name}</div>
            <div className="text-[10px] font-mono text-[var(--color-text-muted)] mt-0.5">{fmtDate(row.created_at)}</div>
          </>
        ) : (
          <span className="text-[var(--color-text-muted)]">—</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        <DispatchBadge status={row.status} />
        {row.tracking_number && <div className="text-[11px] font-mono text-[var(--color-text-muted)] mt-1">{row.tracking_number}</div>}
        {/* Adresaci dekretacji: kto potwierdził odbiór (✓), kto jeszcze nie (→), plus dodawanie
            (adresat / dział obiegu) i usuwanie (dział obiegu) po dekretacji. */}
        <Recipients row={row} userOptions={userOptions} canAdd={canAddRecipients} canRemove={canRemoveRecipients} />
      </td>
      {/* Filenames here are long (numer sprawy + nazwiska); the column has a fixed width and
          each chip truncates — full name stays in the tooltip — so the table fits the screen. */}
      <td className="px-3 py-2.5">
        <div className="w-[240px] max-w-full space-y-1">
        {selectable ? (
          <>
            {row.files.map((f) => (
              <label key={f.id} className="flex items-start gap-1.5">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-[var(--color-primary)]"
                  checked={selectedFiles?.has(f.id) ?? false}
                  onChange={() => onToggleFile?.(f.id)}
                />
                {f.url ? (
                  <a href={f.url} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-[var(--color-primary)] hover:underline break-all">
                    {f.original_filename}
                  </a>
                ) : (
                  <span className="text-[11px] font-mono text-[var(--color-text-secondary)] break-all">{f.original_filename}</span>
                )}
              </label>
            ))}
            {conf?.url && (
              <label className="flex items-start gap-1.5">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-[var(--color-primary)]"
                  checked={selectedFiles?.has(conf.id) ?? false}
                  onChange={() => onToggleFile?.(conf.id)}
                />
                <a href={conf.url} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-600 hover:underline break-all">
                  ↳ zwrotka / UPO
                </a>
              </label>
            )}
            {row.files.length === 0 && !conf?.url && <span className="text-[var(--color-text-muted)]">—</span>}
          </>
        ) : (
          <>
            <AttachmentLinks files={row.files} itemClassName="max-w-full truncate" />
            {conf?.url && (
              <a href={conf.url} target="_blank" rel="noreferrer" className="block text-[11px] text-emerald-600 hover:underline">
                ↳ zwrotka / UPO
              </a>
            )}
          </>
        )}
        </div>
      </td>
      <td className="px-3 py-2.5">
        {/* Outgoing paper → dispatch lifecycle; incoming → dekretacja. Both are
            register-permission mutations, so read-only viewers get no buttons. */}
        {isOutgoing && isPaper && canManage && nextStatuses.length > 0 && (
          <div className="space-y-1.5 min-w-[180px]">
            {(row.status === "queued" || row.status === "prepared") && (
              <input className="input-base text-[11px] py-1" placeholder="nr nadania" value={tracking} onChange={(e) => setTracking(e.target.value)} />
            )}
            {(row.status === "sent" || row.status === "returned") &&
              (file ? (
                <div className="flex items-center gap-1.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
                  <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
                  <button
                    type="button"
                    onClick={() => openPendingFile(file)}
                    className="truncate hover:underline hover:text-[var(--color-primary)]"
                    title={`${file.name} — kliknij, aby podejrzeć`}
                  >
                    {file.name}
                  </button>
                  <button
                    type="button"
                    className="shrink-0 text-[var(--color-primary)] hover:underline"
                    onClick={() => setFile(null)}
                  >
                    usuń
                  </button>
                </div>
              ) : (
                <label className="inline-flex cursor-pointer items-center gap-1.5 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1 text-[10px] font-bold font-mono uppercase tracking-wider text-[var(--color-text-primary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]">
                  <Paperclip size={11} />
                  Załącz zwrotkę
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
              ))}
            <div className="flex flex-wrap gap-1">
              {nextStatuses.map((s) => (
                <Button key={s} size="sm" variant="ghost" onClick={() => doTransition(s)}>
                  {DISPATCH_LABEL[s]}
                </Button>
              ))}
            </div>
          </div>
        )}
        {/* Nr listu dopisany PO wysłaniu — potwierdzenie nadania z poczty rzadko leży na
            biurku w chwili klikania „Wysłane", a wtedy numer wpisywało się wyłącznie razem
            z przejściem statusu. Poza kolejką „Do wysłania" to jedyna droga do numeru. */}
        {isOutgoing && isPaper && canManage && DISPATCHED_REGISTER_STATUSES.has(row.status) && (
          <div className="mt-1.5">
            <RegisterTrackingNumber row={row} />
          </div>
        )}
        {!isOutgoing && row.status === "received" && canManage && (
          <div className="space-y-1.5">
            <FileIncoming
              row={row}
              userOptions={userOptions}
              projectOptions={projectOptions}
              contractors={projectContractors}
            />
            {onEdit && (
              <Button size="sm" variant="ghost" onClick={() => onEdit(row)}>
                <Pencil size={13} />
                Edytuj
              </Button>
            )}
          </div>
        )}
        {/* Samodekretacja — a letter the user is an ADDRESSEE of ("Moja korespondencja") may be
            decreed by them without the register permission, no matter who registered it: their
            own self-registered letter, or one the dział obiegu routed to them at registration.
            Addressed to several people (e.g. the second coordinator) → decreed onto the same
            set of addressees; "Podziel" for whoever wants only their part (backend re-enforces:
            nobody may be dropped). */}
        {!isOutgoing && row.status === "received" && !canManage && isAddressee && (
            <FileIncoming
              row={row}
              userOptions={userOptions}
              projectOptions={assignProjectOptions ?? []}
              contractors={projectContractors}
              selfOnly
            />
          )}
        {/* Filed → only the dekretacja target may confirm receipt. Kto już potwierdził, nie jest
            pytany ponownie, nawet gdy wpis wrócił do „Zadekretowane" po dodaniu adresata. */}
        {!isOutgoing && row.status === "filed" && isAddressee && !iAcknowledged && (
          <AcknowledgeButton row={row} currentUserId={currentUserId} />
        )}
        {!isOutgoing && isAddressee && iAcknowledged && (
          <div className="flex items-center gap-1 text-[11px] text-emerald-600">
            <Check size={11} />
            Potwierdziłeś odbiór
          </div>
        )}
        {onSplit && canSplit && (
          <Button
            size="sm"
            variant="ghost"
            className="mt-1"
            title={
              row.target_user_names.length > 1
                ? "Rozdziel przesyłkę na pozycje — każdy adresat potwierdza odbiór swojej części"
                : "Podziel przesyłkę na osobne pozycje rejestru"
            }
            onClick={() => onSplit(row)}
          >
            <Scissors size={13} />
            Podziel
          </Button>
        )}
        {/* Wycofanie własnego pisma z kolejki wysyłki — autor, dopóki dział obiegu go nie nadał
            (backend sprawdza autorstwo ponownie). Ta sama akcja stoi przy „Moich pismach do
            wysłania" na Zleceniach wydruku; tutaj trafia razem z zakładką „Moje wysyłane",
            żeby autor nie musiał szukać jej na drugiej stronie modułu. */}
        {isOutgoing && isAuthor && (row.status === "queued" || row.status === "prepared") && (
          <div className="mt-1">
            <CancelDispatchButton row={row} />
          </div>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="mt-1"
          title="Historia akcji — kto i kiedy wykonał każdą akcję na wpisie"
          onClick={() => onHistory(row)}
        >
          <History size={13} />
          Historia
        </Button>
      </td>
    </tr>
  );
}

// Zakresy „Mojej korespondencji" — patrz mineScope niżej.
type MineScope = "to_me" | "from_me";

// `mineOnly` powers the "Moja korespondencja" tab — same page, locked to what is mine:
// pisma zadekretowane do mnie ("Do mnie") albo wysłane przeze mnie ("Moje wysyłane").
export function PaperSendPage({ mineOnly = false }: { mineOnly?: boolean }) {
  const navigate = useNavigate();
  // Czytany już przy inicjalizacji stanu (?scope=from_me otwiera „Moje wysyłane"), więc stoi
  // nad nim — poza tym używany jak dotąd do ?badge=.
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserId = useAuthStore((s) => s.user?.id);
  // 'Firmowy obieg dokumentów' — intake + dekretacja. Directors reach the full register
  // with the view-only permission and browse without any mutating actions.
  const canManage = useAuthStore((s) => !!s.user?.has_correspondence_register_permission);
  // Dyrektor / prezes — węższy krąg niż canManage (dział obiegu ma uprawnienie do dziennika, ale
  // adresatów po dekretacji nie dokłada). Backend sprawdza to samo w add-recipients/.
  const isDirector = useAuthStore((s) => !!s.user && (s.user.is_ceo || s.user.role === "director"));
  // Link do projektu ZE tylko z dostępem do DLC_01 — dział obiegu dokumentów często go nie ma —
  // i tylko dla przedsięwzięcia z projektem ZE (target_project_has_ze); reszta zostaje tekstem.
  const canOpenProject = useAuthStore((s) => !!s.user?.accessible_dlc_modules?.includes("dlc_01"));
  // Prezes i dyrektorzy techniczni oglądają CAŁY dziennik (backend: _sees_full_register) i tylko
  // oni dostają filtr po użytkowniku — zawężenie widoku, nie brak dostępu.
  const isLeadership = useAuthStore(
    (s) => !!s.user && (s.user.is_ceo || (s.user.role === "director" && s.user.is_technical_director)),
  );
  // „Moja korespondencja" ma DWA zakresy. „Do mnie" = pisma zadekretowane na mnie (dotychczasowa
  // treść zakładki). „Moje wysyłane" = wpisy WYCHODZĄCE, które sam zarejestrowałem — także te
  // przekazane z DLC_01 (list do właściciela, złożony wniosek). Wpis wychodzący nie jest na nikogo
  // dekretowany, więc bez tego zakresu autor po wysłaniu listów nie widział ich w module nigdzie
  // poza zakładką „Moje pisma do wysłania" na Zleceniach wydruku — i słusznie szukał tutaj.
  const [mineScope, setMineScope] = useState<MineScope>(
    searchParams.get("scope") === "from_me" ? "from_me" : "to_me",
  );
  const fromMe = mineOnly && mineScope === "from_me";
  const [directionFilter, setDirectionFilter] = useState<"" | RegisterDirection>("");
  const [channelFilter, setChannelFilter] = useState<"" | RegisterChannel>("");
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [counterpartyFilter, setCounterpartyFilter] = useState("");
  // Filtr po użytkowniku (adresacie dekretacji) — tylko pełny dziennik kierownictwa.
  // Startuje na nich samych: dziennik otwiera się na własnej korespondencji, a wyczyszczenie
  // filtra pokazuje całość.
  const [userFilter, setUserFilter] = useState(() =>
    !mineOnly && isLeadership ? currentUserId ?? "" : "",
  );
  // Dekretacja filter (full register): "" all · undecreed (received) · decreed (filed+).
  const [decreeFilter, setDecreeFilter] = useState<"" | "undecreed" | "decreed" | "to_contractor">("");
  // Nav-badge quick filter (legend click) — exclusive with the selects: activating it resets
  // them (so the table shows exactly the badge's items), touching any select clears it back.
  const [badgeFilter, setBadgeFilter] = useState<"" | BadgeKey>("");
  const [projectSort, setProjectSort] = useState<"" | "asc" | "desc">("");
  const [adding, setAdding] = useState(false);
  // Edycja niezadekretowanego wpływu — the row being edited (form renders above the table).
  const [editing, setEditing] = useState<CorrespondenceRegister | null>(null);
  // Podział wpływu na pozycje — the row being split (form renders above the table).
  const [splitting, setSplitting] = useState<CorrespondenceRegister | null>(null);
  // Historia akcji — id of the entry whose who-did-what modal is open. Only the id is kept,
  // so the modal reads fresh events from the query cache after any mutation.
  const [historyId, setHistoryId] = useState<string | null>(null);
  // Vademecum (cykl życia dokumentacji) — right-hand panel, open by default; the choice sticks.
  const [showVademecum, setShowVademecum] = useState(() => {
    try {
      return localStorage.getItem("dlc02_vademecum_open") !== "0";
    } catch {
      return true;
    }
  });
  // Bulk file selection ("Moja korespondencja") — file ids, spanning multiple entries.
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

  // Set when the user just reopened the panel — the effect below scrolls it into view.
  const vademecumJustOpened = useRef(false);
  const toggleVademecum = () => {
    const next = !showVademecum;
    try {
      localStorage.setItem("dlc02_vademecum_open", next ? "1" : "0");
    } catch {
      // localStorage unavailable (private mode) — the toggle still works for this session.
    }
    vademecumJustOpened.current = next;
    setShowVademecum(next);
  };

  useEffect(() => {
    if (showVademecum && vademecumJustOpened.current) {
      vademecumJustOpened.current = false;
      // Below the xl breakpoint the panel renders under the table, off-screen — without this
      // scroll a reopen looks like the button did nothing. "nearest" keeps xl+ screens still.
      document.getElementById("dlc02-vademecum")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [showVademecum]);

  const activateBadge = useCallback((key: BadgeKey) => {
    setBadgeFilter(key);
    setDirectionFilter("");
    setChannelFilter("");
    setStatusFilter("");
    setDecreeFilter("");
    setProjectFilter("");
    setCounterpartyFilter("");
    // Also the user filter — a badge counts the WHOLE firm's queue, so leaving it pinned to
    // one person would show fewer rows than the number on the badge.
    setUserFilter("");
    // Liczniki „Mojej korespondencji" mieszkają w różnych zakresach tej samej strony, więc
    // kliknięcie w legendę musi najpierw przełączyć zakładkę — inaczej filtr trafiałby w
    // listę, w której tych wpisów po prostu nie ma.
    if (key === "my_dispatch") setMineScope("from_me");
    if (key === "to_acknowledge") setMineScope("to_me");
  }, []);
  const toggleBadge = (key: BadgeKey) =>
    badgeFilter === key ? setBadgeFilter("") : activateBadge(key);

  // Nagłówek niesie już siedem filtrów — bez tego powrót do pełnego dziennika to
  // przeklikanie każdego z osobna. Czyści też domyślne zaznaczenie siebie w filtrze
  // po użytkowniku: „wyczyść" ma znaczyć „pokaż wszystko".
  const anyFilter =
    !!directionFilter || !!channelFilter || !!statusFilter || !!decreeFilter ||
    !!projectFilter || !!counterpartyFilter || !!userFilter || !!badgeFilter;
  const clearFilters = () => {
    setBadgeFilter("");
    setDirectionFilter("");
    setChannelFilter("");
    setStatusFilter("");
    setDecreeFilter("");
    setProjectFilter("");
    setCounterpartyFilter("");
    setUserFilter("");
  };
  // A legend item whose counter lives on the OTHER tab navigates there with ?badge=…;
  // the effect below picks the param up on arrival and strips it from the URL.
  const navigateBadge = (key: BadgeKey) =>
    navigate(
      `${key === "to_acknowledge" || key === "my_dispatch" ? "/paper-send/mine" : "/paper-send"}?badge=${key}`,
    );

  useEffect(() => {
    const b = searchParams.get("badge");
    if (b && b in BADGE_INFO) {
      activateBadge(b as BadgeKey);
      const next = new URLSearchParams(searchParams);
      next.delete("badge");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, activateBadge]);

  const startEdit = (row: CorrespondenceRegister) => {
    setAdding(false);
    setSplitting(null);
    setEditing(row);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startSplit = (row: CorrespondenceRegister) => {
    setAdding(false);
    setEditing(null);
    setSplitting(row);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleFile = (id: string) =>
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedFiles(new Set());

  // Cycle the Projekt sort: none → asc → desc → none.
  const cycleProjectSort = () =>
    setProjectSort((s) => (s === "" ? "asc" : s === "asc" ? "desc" : ""));

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (directionFilter) p.direction = directionFilter;
    if (channelFilter) p.channel = channelFilter;
    if (statusFilter) p.status = statusFilter;
    if (projectFilter) p.target_project = projectFilter;
    if (counterpartyFilter) p.counterparty = counterpartyFilter;
    if (userFilter) p.target_users = userFilter;
    // „Moje wysyłane" jest self-scoped po AUTORSTWIE (?authored=true), a nie po dekretacji —
    // wpis wychodzący nie ma adresatów, więc ?mine=true nigdy by go nie zwróciło.
    if (mineOnly) {
      if (mineScope === "from_me") p.authored = "true";
      else p.mine = "true";
    }
    return p;
  }, [directionFilter, channelFilter, statusFilter, projectFilter, counterpartyFilter, userFilter, mineOnly, mineScope]);

  const { data, isLoading } = useCorrespondenceRegister(params);
  const historyRow = useMemo(
    () => (historyId ? (data ?? []).find((r) => r.id === historyId) ?? null : null),
    [data, historyId],
  );
  const { data: counts } = useRegisterCounts();
  // Adresaci: pracownicy GK + podwykonawcy z nadanym DLC_02 (mogą dostać wpływ). Sam podwykonawca
  // nie dekretuje na innych, a /users/ i tak odrzuca konta bez Piątku — nie pytamy wtedy wcale.
  const isContractor = useAuthStore((s) => s.user?.role === "contractor");
  const { data: users } = useInternalUsers({ includeDlc02Contractors: true, enabled: !isContractor });
  // Projekty z dziennika (project-options/) zamiast portfolio ZE — działa też dla kont
  // DLC_02 bez modułu DLC_01 (np. dział obiegu dokumentów).
  const { data: portfolio } = useRegisterProjectOptions();
  // Korespondenci obecni w dzienniku — podpowiedzi filtra „Korespondent".
  const { data: counterparties } = useRegisterCounterpartyOptions(
    mineOnly ? (mineScope === "from_me" ? { authored: "true" } : { mine: "true" }) : undefined,
  );
  const { downloadZip } = useRegisterMutations();

  const downloadSelected = () => {
    if (!selectedFiles.size) return;
    downloadZip.mutate([...selectedFiles]);
  };

  // Badge placement mirrors the sidebar (Sidebar.tsx). "Moja korespondencja" carries pisma
  // dekretowane do mnie (also RECEIVED ones pre-assigned at registration), czekające na moje
  // potwierdzenie (to_acknowledge). The firmowa kolejka do dekretacji (to_file) counts INCOMING
  // items still RECEIVED — regardless of pre-assigned addressees — and the Dekretuj action
  // lives on the full "Dziennik korespondencji" register, so that queue badges that tab.
  // Badging it there prevents the "żółty badge, ale pusta lista" symptom for
  // register-permission holders (e.g. dyrektor).
  const hubBadges = useMemo(() => {
    const result: Record<string, HubBadge[]> = {};
    const mine: HubBadge[] = [];
    if (counts?.to_acknowledge)
      mine.push({ count: counts.to_acknowledge, title: BADGE_INFO.to_acknowledge.label });
    // Moje pisma czekające w kolejce wysyłki — ten sam licznik co zakładka „Moje wysyłane"
    // i co badge w menu bocznym (Sidebar.tsx).
    if (counts?.my_dispatch)
      mine.push({ count: counts.my_dispatch, tone: "amber", title: BADGE_INFO.my_dispatch.label });
    if (mine.length) result["/paper-send/mine"] = mine;
    // to_send/returned mirror the sidebar's outgoing-paper counters (amber aggregate + red),
    // so the hub tab explains the same numbers the left menu shows.
    const register: HubBadge[] = [];
    if (counts?.to_file)
      register.push({ count: counts.to_file, tone: "amber", title: BADGE_INFO.to_file.label });
    if (counts?.to_send)
      register.push({ count: counts.to_send, tone: "amber", title: BADGE_INFO.to_send.label });
    if (counts?.returned)
      register.push({ count: counts.returned, tone: "red", title: BADGE_INFO.returned.label });
    if (register.length) result["/paper-send"] = register;
    return Object.keys(result).length ? result : undefined;
  }, [counts]);

  // Legend under the hub nav: every non-zero badge, in hub order. Counters whose items live
  // on the other tab become navigation entries; outgoing-queue counters only ever concern
  // the dział obiegu (canManage) — the backend zeroes them for everyone else anyway.
  const legendItems = useMemo<LegendItem[]>(() => {
    if (!counts) return [];
    const items: LegendItem[] = [];
    const toRegister = { local: !mineOnly, navHint: "Przejdź do Dziennika korespondencji z tym filtrem" };
    if (counts.to_file) items.push({ key: "to_file", count: counts.to_file, ...toRegister });
    if (counts.to_send) items.push({ key: "to_send", count: counts.to_send, ...toRegister });
    if (counts.returned) items.push({ key: "returned", count: counts.returned, ...toRegister });
    if (counts.to_acknowledge)
      items.push({
        key: "to_acknowledge",
        count: counts.to_acknowledge,
        local: mineOnly,
        navHint: "Przejdź do Mojej korespondencji z tym filtrem",
      });
    // Self-scoped — liczy się każdemu, nie tylko działowi obiegu, więc stoi w legendzie
    // obu zakładek (na „Mojej korespondencji" przełącza zakres na „Moje wysyłane").
    if (counts.my_dispatch)
      items.push({
        key: "my_dispatch",
        count: counts.my_dispatch,
        local: mineOnly,
        navHint: "Przejdź do Mojej korespondencji · Moje wysyłane",
      });
    return items;
  }, [counts, mineOnly]);

  const userOptions = useMemo(
    () =>
      (users ?? []).map((u) => ({
        value: u.id,
        label:
          (u.full_name || u.username) +
          (u.role === "contractor" ? ` (podwykonawca${u.contractor_company_name ? ` · ${u.contractor_company_name}` : ""})` : ""),
      })),
    [users],
  );
  const projectOptions = useMemo(
    () => (portfolio ?? []).map((p) => ({ value: p.project_id, label: p.project_name })),
    [portfolio],
  );
  // projectId → firma prowadząca projekt. Dekretacja na taki projekt kieruje pismo do jej
  // rejestru spraw, więc okno musi to pokazać i zablokować wybór osób po naszej stronie.
  const projectContractors = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of portfolio ?? []) if (p.led_contractor_name) map[p.project_id] = p.led_contractor_name;
    return map;
  }, [portfolio]);
  const counterpartyOptions = useMemo(
    () => (counterparties ?? []).map((name) => ({ value: name, label: name })),
    [counterparties],
  );
  // Filtr po użytkowniku stoi obok filtra po korespondencie, a korespondentem bywa osoba
  // (nadawca prywatny) — bez przedrostka oba wyglądałyby po wybraniu tak samo.
  const addresseeOptions = useMemo(
    () => userOptions.map((o) => ({ value: o.value, label: `Adresat: ${o.label}` })),
    [userOptions],
  );
  // projectId → coordinator user ids, for auto-selecting them in the incoming form.
  const projectCoordinators = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const p of portfolio ?? []) map[p.project_id] = p.coordinator_ids ?? [];
    return map;
  }, [portfolio]);
  // Przedsięwzięcia, których jestem koordynatorem — the only ones a plain addressee may pin
  // to an entry (dekretacja do projektu, podział wpływu). Backend re-enforces the same rule.
  const coordinatedProjectOptions = useMemo(() => {
    if (!currentUserId) return [];
    return (portfolio ?? [])
      .filter((p) => (p.coordinator_ids ?? []).includes(currentUserId))
      .map((p) => ({ value: p.project_id, label: p.project_name }));
  }, [portfolio, currentUserId]);
  // Podział wpływu — an addressee only redistributes the shipment between the people it is
  // already decreed to (and onto their own projects); the dział obiegu picks freely.
  const splitUserOptions = useMemo(() => {
    if (canManage) return userOptions;
    if (!splitting) return [];
    return splitting.target_users.map((id, i) => ({
      value: id,
      label: splitting.target_user_names[i] ?? id,
    }));
  }, [canManage, userOptions, splitting]);
  const splitProjectOptions = canManage ? projectOptions : coordinatedProjectOptions;
  const rows = useMemo(() => {
    let base = data ?? [];
    // Legend quick filter — the table shows exactly the rows behind the clicked counter.
    if (badgeFilter) base = base.filter((r) => BADGE_PREDICATE[badgeFilter](r, currentUserId));
    // Dekretacja applies to incoming only: undecreed = awaiting dekretacja (received),
    // decreed = routed to a person/project (filed, or already acknowledged).
    const isUndecreed = (r: CorrespondenceRegister) =>
      r.direction === "incoming" && r.status === "received";
    // Pisma skierowane do firm prowadzących — osobny widok, bo skutki są zewnętrzne.
    if (decreeFilter === "to_contractor") base = base.filter((r) => Boolean(r.led_register_label));
    else if (decreeFilter === "undecreed") base = base.filter(isUndecreed);
    else if (decreeFilter === "decreed")
      base = base.filter(
        (r) => r.direction === "incoming" && (r.status === "filed" || r.status === "acknowledged"),
      );
    if (!projectSort && mineOnly) return base;
    const dir = projectSort === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      // Full register: undecreed incoming always floats to the top (sort is stable,
      // so backend ordering is kept within each group).
      if (!mineOnly) {
        const ua = isUndecreed(a) ? 0 : 1;
        const ub = isUndecreed(b) ? 0 : 1;
        if (ua !== ub) return ua - ub;
      }
      if (!projectSort) return 0;
      // Sort by project name (PL collation); rows without a project always sink to the bottom.
      const an = a.target_project_name ?? "";
      const bn = b.target_project_name ?? "";
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn, "pl") * dir;
    });
  }, [data, projectSort, decreeFilter, badgeFilter, mineOnly, currentUserId]);

  // Every downloadable file id in the current view (attachments + confirmation scans) —
  // powers the "zaznacz wszystkie" header checkbox. Mirrors the checkboxes RegisterRow renders.
  const allFileIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of rows) {
      for (const f of row.files) ids.push(f.id);
      if (row.confirmation_file?.url) ids.push(row.confirmation_file.id);
    }
    return ids;
  }, [rows]);
  const allSelected = allFileIds.length > 0 && allFileIds.every((id) => selectedFiles.has(id));
  const someSelected = allFileIds.some((id) => selectedFiles.has(id));

  // Toggle the whole view: additive/subtractive so a selection carried over from other
  // filters isn't blown away — only the currently visible files are added or removed.
  const toggleSelectAll = () =>
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (allSelected) allFileIds.forEach((id) => next.delete(id));
      else allFileIds.forEach((id) => next.add(id));
      return next;
    });

  return (
    <div className="p-3 md:p-4">
      <PageHeader
        title={mineOnly ? "Moja korespondencja" : "Dziennik korespondencji"}
        subtitle={
          mineOnly
            ? fromMe
              ? "Pisma wysłane przeze mnie · DLC_02"
              : "Pisma zadekretowane do mnie · DLC_02"
            : "Firmowy rejestr nadawczo-odbiorczy · DLC_02"
        }
        actions={
          // Dwie grupy: FILTRY (zawężają listę) i AKCJE (otwierają formularz / panel).
          // W jednym rzędzie zlewały się w ciąg jednakowych kontrolek, więc akcje dostają
          // WŁASNY wiersz (basis-full) przy prawej krawędzi — oddzielenie nie zależy wtedy
          // od tego, czy filtry akurat zawinęły się przy danej szerokości okna.
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* Manual selects and the legend quick filter are exclusive — see badgeFilter. */}
            <select
              className="input-base sm:max-w-[160px]"
              value={directionFilter}
              onChange={(e) => {
                setBadgeFilter("");
                setDirectionFilter(e.target.value as "" | RegisterDirection);
              }}
            >
              <option value="">Wszystkie kierunki</option>
              <option value="incoming">Przychodzące</option>
              <option value="outgoing">Wychodzące</option>
            </select>
            <select
              className="input-base sm:max-w-[160px]"
              value={channelFilter}
              onChange={(e) => {
                setBadgeFilter("");
                setChannelFilter(e.target.value as "" | RegisterChannel);
              }}
            >
              <option value="">Wszystkie kanały</option>
              {REGISTER_CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="input-base sm:max-w-[180px]"
              value={statusFilter}
              onChange={(e) => {
                setBadgeFilter("");
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">Wszystkie statusy</option>
              {Object.entries(DISPATCH_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            {/* Dekretacja filter — full register only (directors + dział obiegu). */}
            {!mineOnly && (
              <select
                className="input-base sm:max-w-[180px]"
                value={decreeFilter}
                onChange={(e) => {
                  setBadgeFilter("");
                  setDecreeFilter(e.target.value as "" | "undecreed" | "decreed" | "to_contractor");
                }}
              >
                <option value="">Dekretacja: wszystkie</option>
                <option value="undecreed">Niezadekretowane</option>
                <option value="decreed">Zadekretowane</option>
                <option value="to_contractor">Przekazane firmom</option>
              </select>
            )}
            <div className="w-full sm:w-[200px]">
              <SearchableSelect
                options={projectOptions}
                value={projectFilter}
                placeholder="Wszystkie projekty"
                onChange={(e) => {
                  setBadgeFilter("");
                  setProjectFilter(e.target.value);
                }}
              />
            </div>
            <div className="w-full sm:w-[200px]">
              <SearchableSelect
                options={counterpartyOptions}
                value={counterpartyFilter}
                placeholder="Wszyscy korespondenci"
                onChange={(e) => {
                  setBadgeFilter("");
                  setCounterpartyFilter(e.target.value);
                }}
              />
            </div>
            {/* Filtr po użytkowniku — pełny dziennik prezesa / dyrektora technicznego.
                Domyślnie wskazuje ich samych; „Wszyscy użytkownicy" pokazuje cały dziennik. */}
            {!mineOnly && isLeadership && (
              <div className="w-full sm:w-[200px]">
                <SearchableSelect
                  options={addresseeOptions}
                  value={userFilter}
                  placeholder="Wszyscy użytkownicy"
                  onChange={(e) => {
                    setBadgeFilter("");
                    setUserFilter(e.target.value);
                  }}
                />
              </div>
            )}
            {anyFilter && (
              <Button size="sm" variant="ghost" onClick={clearFilters} title="Pokaż cały dziennik">
                Wyczyść filtry
              </Button>
            )}
          </div>

          <div className="flex basis-full flex-wrap items-center justify-end gap-2 border-t border-dashed border-[var(--color-border)] pt-2">
            {/* Full register: intake for the dział obiegu. "Moja korespondencja": a plain user
                registers a letter FOR THEMSELVES (self-dekretacja); permission holders use the
                full register instead. */}
            {(mineOnly ? !canManage && !fromMe : canManage) && (
              <Button
                size="sm"
                variant={adding ? "ghost" : "secondary"}
                onClick={() => {
                  setEditing(null);
                  setSplitting(null);
                  setAdding((a) => !a);
                }}
              >
                {adding ? "Anuluj" : "+ Rejestracja wpływu"}
              </Button>
            )}
            {/* Ghost while the panel is visible; secondary (clearly clickable) once hidden,
                so there is always an obvious way to bring the vademecum back. */}
            <Button
              size="sm"
              variant={showVademecum ? "ghost" : "secondary"}
              onClick={toggleVademecum}
              title="Cykl życia dokumentacji w DLC_02 — jak dla ludzi"
            >
              <BookOpen size={13} />
              {showVademecum ? "Ukryj vademecum" : "Vademecum"}
            </Button>
          </div>
          </div>
        }
      />

      <HubNav links={DLC02_HUB_LINKS} badges={hubBadges} />

      {mineOnly && (
        <Tabs
          active={mineScope}
          onChange={(k) => {
            setBadgeFilter("");
            setMineScope(k);
          }}
          tabs={[
            {
              key: "to_me",
              label: "Do mnie",
              count: counts?.to_acknowledge ?? 0,
              countTone: "danger",
            },
            {
              key: "from_me",
              label: "Moje wysyłane",
              count: counts?.my_dispatch ?? 0,
              countTone: "warn",
            },
          ]}
        />
      )}

      <BadgeLegend
        items={legendItems}
        activeKey={badgeFilter}
        onToggle={toggleBadge}
        onNavigate={navigateBadge}
      />

      {/* Main column + right-hand Vademecum panel (stacks below the table on small screens). */}
      <div className="xl:flex xl:items-start xl:gap-5">
      <div className="flex-1 min-w-0">

      {mineOnly && selectedFiles.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
          <span className="text-[13px] text-[var(--color-text-secondary)]">
            Zaznaczono plików: <strong className="text-[var(--color-text-primary)]">{selectedFiles.size}</strong>
          </span>
          <Button size="sm" onClick={downloadSelected} disabled={downloadZip.isPending}>
            {downloadZip.isPending ? "Pakowanie…" : "Pobierz zaznaczone (ZIP)"}
          </Button>
          <button
            type="button"
            onClick={clearSelection}
            className="text-[12px] text-[var(--color-text-muted)] hover:underline"
          >
            Wyczyść
          </button>
          {downloadZip.isError && (
            <span className="text-[12px] text-red-600">Nie udało się pobrać plików.</span>
          )}
        </div>
      )}

      {adding && (
        <IncomingForm
          userOptions={userOptions}
          projectOptions={mineOnly ? coordinatedProjectOptions : projectOptions}
          projectCoordinators={projectCoordinators}
          selfOnly={mineOnly}
          currentUserId={currentUserId}
          onDone={() => setAdding(false)}
        />
      )}

      {editing && !mineOnly && (
        // Keyed by row id so switching between rows resets the prefilled form state.
        <IncomingForm
          key={editing.id}
          editRow={editing}
          userOptions={userOptions}
          projectOptions={projectOptions}
          projectCoordinators={projectCoordinators}
          onDone={() => setEditing(null)}
        />
      )}

      {splitting && (
        // Keyed by row id so switching between rows resets the draft parts.
        <SplitIncomingForm
          key={splitting.id}
          row={splitting}
          userOptions={splitUserOptions}
          projectOptions={splitProjectOptions}
          // An addressee-driven split must leave every position decreed to somebody —
          // otherwise a part would land in a register nobody watches.
          requireTargetUser={!canManage}
          onDone={() => setSplitting(null)}
        />
      )}

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Kierunek / kanał</Th>
              <Th>Korespondent / sprawa</Th>
              <Th>
                <button
                  type="button"
                  onClick={cycleProjectSort}
                  title="Sortuj po projekcie"
                  className="inline-flex items-center gap-1 font-mono uppercase tracking-widest hover:text-[var(--color-text-secondary)]"
                >
                  Projekt
                  <span className="text-[0.7rem]">{projectSort === "asc" ? "▲" : projectSort === "desc" ? "▼" : "↕"}</span>
                </button>
              </Th>
              <Th>Autor</Th>
              <Th>Status / cel</Th>
              <Th>
                {mineOnly ? (
                  <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Zaznacz wszystkie dokumenty w widoku">
                    <input
                      type="checkbox"
                      className="accent-[var(--color-primary)]"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && someSelected;
                      }}
                      disabled={allFileIds.length === 0}
                      onChange={toggleSelectAll}
                    />
                    Dokumenty
                  </label>
                ) : (
                  "Dokumenty"
                )}
              </Th>
              <Th>Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <RegisterRow
                key={row.id}
                row={row}
                userOptions={userOptions}
                projectOptions={projectOptions}
                projectContractors={projectContractors}
                currentUserId={currentUserId}
                canManage={canManage}
                isDirector={isDirector}
                canOpenProject={canOpenProject}
                selectable={mineOnly}
                selectedFiles={selectedFiles}
                onToggleFile={toggleFile}
                // Pełny słownik przedsięwzięć ZE na obu zakładkach — projekt podpina autor
                // wpisu, adresat dekretacji albo dział obiegu (backend re-enforces).
                assignProjectOptions={projectOptions}
                onEdit={mineOnly || !canManage ? undefined : startEdit}
                onSplit={startSplit}
                onHistory={(r) => setHistoryId(r.id)}
              />
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center section-label tracking-wide">
                  {anyFilter
                    ? "Brak pozycji dla wybranych filtrów."
                    : fromMe
                      ? "Nie wysłałeś jeszcze żadnego pisma."
                      : "Brak wpisów w dzienniku."}
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      </div>
      {showVademecum && (
        <aside id="dlc02-vademecum" className="mt-4 xl:mt-0 xl:w-[320px] xl:shrink-0 xl:sticky xl:top-4">
          <VademecumPanel onClose={toggleVademecum} />
        </aside>
      )}
      </div>

      <Modal open={!!historyRow} onClose={() => setHistoryId(null)} title="Historia akcji" size="lg">
        {historyRow && <EntryHistory row={historyRow} />}
      </Modal>
    </div>
  );
}
